import { APIResponse, apiService } from "../api";

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  type: "membership" | "room_rental";
  price: string;
  duration_days: number;
  max_users: number;
  features: string[];
  is_active: boolean;
  show_price: boolean;
  has_offer: boolean;
  offer_name: string | null;
  discount_percentage: number | null;
  offer_price: string | null;
  offer_start_date: string | null;
  offer_end_date: string | null;
  created_at: string;
}

export interface SubscriptionQueryParams {
  pageNumber?: number;
  limit?: number;
  type?: "membership" | "room_rental";
  is_active?: boolean;
  search?: string;
}

class SubscriptionService {
  private readonly baseUrl = "/subscription_plans/v1/dashboard";

  async getSubscriptionPlans(
    params?: SubscriptionQueryParams,
  ): Promise<APIResponse<SubscriptionPlan>> {
    const queryParams = {
      pageNumber: params?.pageNumber || 1,
      limit: params?.limit || 100,
      ...(params?.type && { type: params.type }),
      ...(params?.is_active !== undefined && { is_active: params.is_active }),
      ...(params?.search && { search: params.search }),
    };
    return apiService.get<SubscriptionPlan>(this.baseUrl, {
      params: queryParams,
    });
  }

  async getMembershipPlans(
    params?: Omit<SubscriptionQueryParams, "type">,
  ): Promise<APIResponse<SubscriptionPlan>> {
    return this.getSubscriptionPlans({ ...params, type: "membership" });
  }

  async getRoomRentalPlans(
    params?: Omit<SubscriptionQueryParams, "type">,
  ): Promise<APIResponse<SubscriptionPlan>> {
    return this.getSubscriptionPlans({ ...params, type: "room_rental" });
  }

  async getSubscriptionPlan(
    id: string,
  ): Promise<APIResponse<SubscriptionPlan>> {
    return apiService.get<SubscriptionPlan>(`${this.baseUrl}/${id}`);
  }

  // Helper to parse price string to number
  parsePrice(price: string): number {
    return parseFloat(price);
  }

  // Helper to calculate duration display (SMART VERSION)
  getDurationDisplay(durationDays: number): string {
    // Fixed common durations
    if (durationDays === 1) return "per day";
    if (durationDays === 7) return "per week";
    if (durationDays === 30) return "per month";
    if (durationDays === 90) return "per quarter";
    if (durationDays === 180) return "per 6 months";
    if (durationDays === 360 || durationDays === 365) return "per year";

    // Dynamic conversion for other values
    // Check if it's a multiple of 30 (months)
    if (durationDays % 30 === 0) {
      const months = durationDays / 30;
      return `${months} month${months > 1 ? "s" : ""}`;
    }

    // Check if it's a multiple of 7 (weeks)
    if (durationDays % 7 === 0) {
      const weeks = durationDays / 7;
      return `${weeks} week${weeks > 1 ? "s" : ""}`;
    }

    // Default: show days
    return `${durationDays} day${durationDays > 1 ? "s" : ""}`;
  }

  // Helper to calculate effective price (with offer if available)
  getEffectivePrice(plan: SubscriptionPlan): number {
    if (plan.has_offer && plan.offer_price) {
      return this.parsePrice(plan.offer_price);
    }
    return this.parsePrice(plan.price);
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
